<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useOnixFlowsStore } from 'dashboard/store/onix/flows';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const flowsStore = useOnixFlowsStore();

const newName = ref('');

onMounted(() => {
  flowsStore.fetch();
});

const openFlow = flowId => {
  router.push(accountScopedRoute('onix_flow_canvas', { flowId }));
};

const createFlow = async () => {
  const name = newName.value.trim() || t('ONIX.FLOWS.DEFAULT_NAME');
  const data = await flowsStore.create({
    flow: {
      name,
      entry_node_key: 'start',
      enabled: false,
      nodes: [
        {
          key: 'start',
          node_type: 'respond',
          position_x: 120,
          position_y: 120,
          config: {},
        },
      ],
      edges: [],
    },
  });
  if (data?.id) {
    newName.value = '';
    openFlow(data.id);
  }
};
</script>

<template>
  <div class="flex flex-col w-full h-full gap-4 p-6 overflow-auto">
    <div class="flex items-center justify-between gap-4">
      <div>
        <h1 class="text-xl font-semibold text-n-slate-12">
          {{ t('ONIX.FLOWS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('ONIX.FLOWS.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model="newName"
          type="text"
          class="h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12"
          :placeholder="t('ONIX.FLOWS.NAME_PLACEHOLDER')"
          @keyup.enter="createFlow"
        />
        <Button
          :label="t('ONIX.FLOWS.CREATE')"
          size="sm"
          :is-loading="flowsStore.uiFlags.creatingItem"
          @click="createFlow"
        />
      </div>
    </div>

    <div v-if="flowsStore.uiFlags.fetchingList" class="text-sm text-n-slate-11">
      {{ t('ONIX.COMMON.LOADING') }}
    </div>

    <ul v-else class="flex flex-col gap-2 m-0 p-0 list-none">
      <li
        v-for="flow in flowsStore.getFlows"
        :key="flow.id"
        class="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-1 cursor-pointer"
        @click="openFlow(flow.id)"
      >
        <div>
          <div class="font-medium text-n-slate-12">
            {{ flow.name }}
          </div>
        </div>
        <span
          class="text-xs px-2 py-0.5 rounded-full"
          :class="
            flow.enabled
              ? 'bg-n-teal-3 text-n-teal-11'
              : 'bg-n-slate-3 text-n-slate-11'
          "
        >
          {{
            flow.enabled ? t('ONIX.FLOWS.ENABLED') : t('ONIX.FLOWS.DISABLED')
          }}
        </span>
      </li>
      <li
        v-if="!flowsStore.getFlows.length"
        class="text-sm text-n-slate-11 px-1"
      >
        {{ t('ONIX.FLOWS.EMPTY') }}
      </li>
    </ul>
  </div>
</template>
